import {
  AnchorHTMLAttributes,
  CSSProperties,
  ReactElement,
  ReactNode,
  Ref,
  forwardRef,
  isValidElement,
} from 'react';
import { Link as RRLink } from 'react-router';

export interface UrlObject {
  pathname?: string;
  query?: Record<string, string | number | boolean | undefined>;
  hash?: string;
}

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string | UrlObject;
  as?: string | UrlObject;
  passHref?: boolean;
  prefetch?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  replace?: boolean;
  locale?: string | false;
  legacyBehavior?: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const isExternal = (to: string | undefined): boolean =>
  typeof to === 'string' && /^(https?:|mailto:|tel:|#)/.test(to);

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    href,
    as: _as,
    passHref: _passHref,
    prefetch: _prefetch,
    scroll: _scroll,
    shallow: _shallow,
    replace,
    locale: _locale,
    legacyBehavior: _legacyBehavior,
    children,
    ...rest
  },
  ref
) {
  const to: string =
    typeof href === 'object' ? href?.pathname || '/' : href;

  let mergedRest: Record<string, unknown> = rest as Record<string, unknown>;
  let mergedChildren: ReactNode = children;
  if (isValidElement(children) && children.type === 'a') {
    const childEl = children as ReactElement<AnchorHTMLAttributes<HTMLAnchorElement>>;
    mergedRest = { ...childEl.props, ...rest } as Record<string, unknown>;
    mergedChildren = childEl.props.children;
  }

  if (!to || isExternal(to)) {
    return (
      <a ref={ref} href={to || '#'} {...(mergedRest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {mergedChildren}
      </a>
    );
  }

  return (
    <RRLink
      ref={ref as Ref<HTMLAnchorElement>}
      to={to}
      replace={replace}
      {...(mergedRest as AnchorHTMLAttributes<HTMLAnchorElement>)}
    >
      {mergedChildren}
    </RRLink>
  );
});

export default Link;
